import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { Mail } from "lucide-react";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Account creato correttamente");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.friendlyMessage || "Errore durante la registrazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative bg-primary overflow-hidden">
        <div className="absolute inset-0 grain-bg opacity-[0.15]" />
        <div className="relative h-full flex flex-col justify-between p-12 text-primary-foreground">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-primary-foreground/10 border border-primary-foreground/20 grid place-items-center">
              <Mail className="w-4 h-4" />
            </div>
            <div>
              <div className="font-heading text-lg">TECSend</div>
              <div className="text-[10px] uppercase tracking-[0.25em] opacity-70">
                Newsletter M365
              </div>
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] opacity-70 mb-4">
              Inizia in 2 minuti
            </p>
            <h2 className="font-heading text-4xl md:text-5xl leading-[1.1] mb-6">
              Il tuo Microsoft 365, potenziato per le newsletter.
            </h2>
            <ul className="space-y-3 text-primary-foreground/75 max-w-md">
              <li className="flex gap-3">
                <span className="text-primary-foreground">→</span>
                Import CSV con mappatura colonne e deduplica
              </li>
              <li className="flex gap-3">
                <span className="text-primary-foreground">→</span>
                Editor HTML con anteprima dal vivo e template
              </li>
              <li className="flex gap-3">
                <span className="text-primary-foreground">→</span>
                Tracciamento aperture e click integrato
              </li>
            </ul>
          </div>
          <div className="text-xs opacity-60">
            © {new Date().getFullYear()} TECSend
          </div>
        </div>
      </div>

      <div className="grid place-items-center p-8">
        <form
          onSubmit={submit}
          className="w-full max-w-sm space-y-6"
          data-testid="register-form"
        >
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">
              Nuovo account
            </p>
            <h1 className="font-heading text-3xl font-medium tracking-tight">
              Crea il tuo account
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Bastano un'email e una password. Nessuna carta richiesta.
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome (opzionale)</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="register-name"
                placeholder="Come ti chiami"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email"
                placeholder="nome@azienda.it"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password"
                placeholder="Almeno 6 caratteri"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={loading}
            data-testid="register-submit"
          >
            {loading ? "Creazione…" : "Crea account"}
          </Button>

          <div className="text-sm text-muted-foreground">
            Hai già un account?{" "}
            <Link
              to="/login"
              className="text-primary font-medium hover:underline"
              data-testid="link-login"
            >
              Accedi
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
